// packages/shared-hooks/src/auth/useLogin.ts
// Web refresh-token containment boundary.
//
// WebLoginResponseSchema is transport-pinned for the web SPA: it rejects
// any body `refreshToken` at the Zod boundary because web is cookie-only
// (the refresh token rides the httpOnly Secure SameSite=Strict cookie
// exclusively and is never echoed in the JSON body). The destructure
// below is defense-in-depth alongside the schema — together they
// guarantee that TanStack Query's `mutation.data` provably cannot ever
// surface a refresh token even if a future backend regression
// reintroduced one.
import { useMutation } from '@tanstack/react-query';
import { WebLoginResponseSchema, type LoginInput, type WebLoginResponse } from '@magimp/shared';
import { useApiClient } from '../api/context.js';

// `refreshToken?: never` is a TypeScript-level guarantee: anything that
// tries to read result.refreshToken fails to compile.
export type WebLoginResult = {
  accessToken: string;
  user: WebLoginResponse['user'];
  refreshToken?: never;
};

export function useLogin() {
  const client = useApiClient();
  return useMutation<WebLoginResult, unknown, LoginInput>({
    mutationFn: async (input) => {
      const { accessToken, user } = await client.post<WebLoginResponse>(
        '/auth/login',
        input,
        {},
        WebLoginResponseSchema,
      );
      return { accessToken, user }; // typed as WebLoginResult — refreshToken?: never
    },
  });
}
